// components/chakra/NavbarWithCallToAction/NavbarWithCallToAction.tsx

import {
  Box,
  Flex,
  HStack,
  VStack,
  IconButton,
  Link,
  Text,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  useDisclosure,
  Divider,
} from "@chakra-ui/react";
import { Logo } from "../../../Logo";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { FiLogOut, FiMenu } from 'react-icons/fi';
import { useState, useEffect } from 'react';
import './navbar.css';

export const NavbarWithCallToAction = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOverBlue, setIsOverBlue] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    const handleScroll = () => {
      // Try multiple scroll sources
      const scrollTop = Math.max(
        window.scrollY || 0,
        window.pageYOffset || 0,
        document.documentElement.scrollTop || 0,
        document.body.scrollTop || 0
      );
      setIsScrolled(scrollTop > 10);

      // Detect when navbar is over blue section
      const viewportHeight = window.innerHeight;
      const blueStart = viewportHeight * 0.4;
      const blueEnd = viewportHeight * 1.2;

      // Pages with navy hero at the top need white navbar text from scrollTop=0
      const blueHeroPages = ['/our-facilities', '/corporate-services'];
      const hasBlueHero = blueHeroPages.includes(location.pathname);

      if (hasBlueHero && scrollTop < blueEnd) {
        setIsOverBlue(true);
      } else {
        setIsOverBlue(scrollTop > blueStart && scrollTop < blueEnd);
      }
    };

    // Run on mount to set initial state
    handleScroll();

    // Listen to all possible scroll events
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true });
    document.body.addEventListener('scroll', handleScroll, { passive: true });
    document.documentElement.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);
      document.body.removeEventListener('scroll', handleScroll);
      document.documentElement.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleLogoClick = () => {
    navigate("/");
    onClose(); // Close mobile menu if open
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    onClose(); // Close mobile menu if open
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose(); // Close mobile menu when navigating
  };

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about-us' },
    { label: 'Our Services', path: '/corporate-services' },
    { label: 'Our Facilities', path: '/our-facilities' },
  ];

  const contactItem = { label: 'Contact', path: '/contact-us' };

  return (
    <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        zIndex={1000}
        width="100%"
        transition="all 0.15s ease-out"
        bg={isScrolled ? "rgba(255, 255, 255, 0.75)" : "transparent"}
        backdropFilter={isScrolled ? "blur(30px) saturate(150%)" : "none"}
        borderBottom={isScrolled ? "1px solid rgba(0, 0, 0, 0.2)" : "none"}
        boxShadow={isScrolled ? "0 2px 20px rgba(0, 0, 0, 0.15)" : "none"}
        sx={{
          WebkitBackdropFilter: isScrolled ? "blur(30px) saturate(150%)" : "none",
        }}
      >
        <Box position="relative" zIndex={1} width="100%">
          <Flex
            minH={{ base: "70px", sm: "80px", md: "100px", lg: "130px" }}
            pt={{ base: 4, sm: 6, md: 10, lg: 16 }}
            pb={{ base: 2, md: 4 }}
            align="center"
            justify="space-between"
            width="100%"
            pl={{ base: "5vw", md: "10vw", lg: "12vw" }}
            pr={{ base: "5vw", md: "5vw", lg: "5vw" }}
          >
            {/* Desktop Navigation - Logo aligned with content below */}
            <HStack
              flex={1}
              justify="space-between"
              display={{ base: "none", md: "flex" }}
              alignItems="center"
            >
              {/* Logo */}
              <Link onClick={handleLogoClick} style={{ cursor: "pointer", textDecoration: "none" }}>
                <Logo isLight={isOverBlue && !isScrolled} />
              </Link>

              {/* Nav Items Group */}
              <HStack spacing={{ md: 10, lg: 16, xl: 20 }} alignItems="center">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    style={{ textDecoration: "none" }}
                  >
                    <Text
                      fontSize={{ md: "md", lg: "lg", xl: "xl" }}
                      fontWeight={location.pathname === item.path ? "600" : "500"}
                      color={location.pathname === item.path ? "#FF6600" : (isOverBlue && !isScrolled ? "#FFFFFF" : "#03177A")}
                      _hover={{ color: "#FF6600" }}
                      transition="all 0.3s ease"
                      cursor="pointer"
                      fontFamily="Montserrat, sans-serif"
                      position="relative"
                      _after={location.pathname === item.path ? {
                        content: '""',
                        position: "absolute",
                        bottom: "-4px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "6px",
                        height: "6px",
                        borderRadius: "full",
                        bg: "#FF6600",
                      } : {}}
                    >
                      {item.label}
                    </Text>
                  </Link>
                ))}

                {/* Contact Button */}
                <Box
                  as="button"
                  onClick={() => handleNavigation(contactItem.path)}
                  bg="#FF6600"
                  color="#FFFFFF"
                  px={6}
                  py={2}
                  borderRadius="full"
                  fontSize={{ md: "md", lg: "lg", xl: "xl" }}
                  fontWeight="500"
                  fontFamily="Montserrat, sans-serif"
                  _hover={{
                    bg: "#CC4902",
                    transform: "translateY(-1px)",
                  }}
                  transition="all 0.3s ease"
                  border="none"
                  cursor="pointer"
                >
                  {contactItem.label}
                </Box>
              </HStack>

              {/* Desktop Logout Button */}
              {isAuthenticated && (
                <IconButton
                  aria-label="Logout"
                  icon={<FiLogOut />}
                  size="sm"
                  variant="ghost"
                  color="red.400"
                  onClick={handleLogout}
                  _hover={{
                    color: "red.500",
                    bg: "red.50",
                  }}
                />
              )}
            </HStack>

            {/* Mobile: Logo + Hamburger */}
            <Flex display={{ base: "flex", md: "none" }} justify="space-between" align="center" width="100%" px={4}>
              <Link onClick={handleLogoClick} style={{ cursor: "pointer", textDecoration: "none" }}>
                <Logo isLight={isOverBlue && !isScrolled} />
              </Link>
              <IconButton
                aria-label="Open navigation menu"
                icon={<FiMenu />}
                size="lg"
                variant="ghost"
                color={isOverBlue && !isScrolled ? "#FFFFFF" : "#03177A"}
                onClick={onOpen}
                _hover={{
                  color: "#FF6600",
                  bg: "rgba(255, 102, 0, 0.1)",
                }}
                _active={{
                  bg: "rgba(255, 102, 0, 0.2)",
                }}
                transition="all 0.3s ease"
              />
            </Flex>
          </Flex>
        </Box>

        {/* Mobile Navigation Drawer */}
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xs">
          <DrawerOverlay bg="rgba(3, 23, 122, 0.3)" />
          <DrawerContent
            bg="rgba(235, 235, 235, 0.95)"
            borderLeft="1px solid rgba(3, 23, 122, 0.2)"
            backdropFilter="blur(20px)"
          >
            <DrawerCloseButton
              color="#03177A"
              size="lg"
              _hover={{
                color: "#FF6600",
                bg: "rgba(255, 102, 0, 0.1)",
              }}
            />
            <DrawerBody pt={20} px={6}>
              <VStack spacing={8} align="start" w="100%">
                {/* Mobile Logo */}
                <Box onClick={handleLogoClick} cursor="pointer" mb={4}>
                  <Logo />
                </Box>

                <Divider borderColor="rgba(3, 23, 122, 0.2)" />

                {/* Mobile Navigation Items */}
                <VStack spacing={6} align="start" w="100%">
                  {navItems.map((item) => (
                    <Link
                      key={item.path}
                      onClick={() => handleNavigation(item.path)}
                      style={{ textDecoration: "none", width: "100%" }}
                    >
                      <Text
                        fontSize="xl"
                        fontWeight={location.pathname === item.path ? "700" : "500"}
                        color={location.pathname === item.path ? "#FF6600" : "#03177A"}
                        _hover={{
                          color: "#FF6600",
                          transform: "translateX(10px)",
                        }}
                        transition="all 0.3s ease"
                        cursor="pointer"
                        fontFamily="Montserrat, sans-serif"
                        py={3}
                        borderBottom="1px solid rgba(3, 23, 122, 0.1)"
                        w="100%"
                      >
                        {item.label}
                      </Text>
                    </Link>
                  ))}

                  {/* Mobile Contact Button */}
                  <Box
                    as="button"
                    onClick={() => handleNavigation(contactItem.path)}
                    bg="#FF6600"
                    color="#FFFFFF"
                    px={8}
                    py={3}
                    borderRadius="full"
                    fontSize="lg"
                    fontWeight="500"
                    fontFamily="Montserrat, sans-serif"
                    _hover={{
                      bg: "#CC4902",
                    }}
                    transition="all 0.3s ease"
                    border="none"
                    cursor="pointer"
                    mt={4}
                  >
                    {contactItem.label}
                  </Box>
                </VStack>

                {/* Mobile Logout Button */}
                {isAuthenticated && (
                  <>
                    <Divider borderColor="rgba(3, 23, 122, 0.2)" mt={8} />
                    <Box mt={4} w="100%">
                      <HStack
                        spacing={3}
                        p={3}
                        cursor="pointer"
                        color="red.400"
                        onClick={handleLogout}
                        _hover={{
                          color: "red.500",
                          bg: "rgba(239, 68, 68, 0.1)",
                        }}
                        transition="all 0.3s ease"
                        borderRadius="md"
                        w="100%"
                      >
                        <FiLogOut size={20} />
                        <Text fontSize="xl" fontFamily="Montserrat, sans-serif">
                          Logout
                        </Text>
                      </HStack>
                    </Box>
                  </>
                )}
              </VStack>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
    </Box>
  );
};